import React from "react";
import PropTypes from "prop-types";

import { useSearchParams } from "routes/hooks";

import { useDispatchUserSubscription, usePlan, usePlanState } from "./hooks";
import {
  BillSummary,
  Disclaimer,
  Loader,
  RateSelector,
  Section,
  StaticBulletList,
  SubscriptionCreator,
} from "./components";

export function BillingPage({ onUpdate }) {
  const updateSubscription = useDispatchUserSubscription();
  const [searchParams] = useSearchParams();
  const { plans, loading } = usePlan(searchParams?.plan);
  const [selectedPlan, setSelectedPlan] = usePlanState(
    plans?.planOptions,
    searchParams?.billingPeriod,
    onUpdate
  );

  if (loading) {
    return (
      <div data-test="loading-container">
        <Loader active />
      </div>
    );
  }

  if (!selectedPlan) {
    return (
      <div>
        <p>A plan has not been selected. This is unexpected.</p>
        <p>Please reach out to customer support.</p>
      </div>
    );
  }

  return (
    <div
      data-test="billing-container"
      style={{ display: "flex", alignItems: "center", flexDirection: "column" }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "50px",
        }}
      >
        <Section style={{ width: "525px" }}>
          <Section.Header>{plans.title}</Section.Header>

          <StaticBulletList items={plans.featureList} />

          <div style={{ marginTop: "50px" }} data-test="rate-container">
            <RateSelector
              planOptions={plans.planOptions}
              onSelected={(plan) => {
                onUpdate(plan);
                setSelectedPlan(plan);
              }}
              selected={selectedPlan}
            />
            <BillSummary
              isTrial={Boolean(selectedPlan?.trial)}
              currency={selectedPlan?.pricing?.currency}
              billedToday={selectedPlan?.pricing?.billedToday}
              billingPeriod={selectedPlan?.pricing?.billingPeriod}
              monthlyPrice={selectedPlan?.pricing?.monthlyPrice}
            />
          </div>
        </Section>
        <Section style={{ width: "525px" }}>
          <Section.Header>Payment Information</Section.Header>

          <div data-test="subscription-creator-container">
            <SubscriptionCreator
              plan={selectedPlan?.planId}
              onCreated={({ msg }) => {
                updateSubscription(msg);
              }}
            />
          </div>
        </Section>
      </div>

      {selectedPlan?.trial && (
        <Disclaimer
          value={selectedPlan?.pricing?.firstBillPrice}
          currency={selectedPlan?.pricing?.currency}
          period={selectedPlan?.trial?.period}
        />
      )}
    </div>
  );
}

BillingPage.propTypes = { onUpdate: PropTypes.func };
BillingPage.defaultProps = { onUpdate: () => {} };

export default BillingPage;
